import { useTranslation } from "react-i18next";
import Typography from "components/Typography";
import Skeleton from "@mui/material/Skeleton";
import { LogoContainer, LogoImg } from "components/MessageCard/MessageCard.components";
import outputLogoStripes from "assets/images/output-logo-180x180.svg";
import outputLogoStripesBlack from "assets/images/output-logo-black-180x180.svg";
import { Container, AnimationContainer } from "./components";
import useMergeProps from "hooks/useMergeProps";
import type { DivProps } from "components/primitives/Div";

interface FetchingFallbackProps {
  children?: React.ReactNode;
  size?: "medium" | "large" | "xlarge";
  bgColor?: string;
  variant?: "primary" | "secondary";
  slotProps?: {
    root?: DivProps;
  };
}

const SIZE_MAP = {
  medium: 32,
  large: 44,
  xlarge: 56,
} as const;

const LOGO_SIZE_MAP = {
  medium: 24,
  large: 32,
  xlarge: 40,
} as const;

const INITIAL_PROPS = {
  size: "medium",
  bgColor: "background.default",
  variant: "primary",
} as const;

function FetchingFallback(props: FetchingFallbackProps) {
  const mergedProps = useMergeProps(INITIAL_PROPS, props);
  const { t } = useTranslation();

  return (
    <Container
      {...mergedProps.slotProps?.root}
      role="status"
      aria-live="polite"
      aria-label={t("global.loading_text")}
      sx={{ ...mergedProps.slotProps?.root?.sx, backgroundColor: mergedProps.bgColor }}
    >
      <AnimationContainer>
        <Skeleton
          animation="pulse"
          variant="circular"
          width={SIZE_MAP[mergedProps.size]}
          height={SIZE_MAP[mergedProps.size]}
          sx={(theme) => ({
            position: "absolute",
            background: mergedProps.variant === "primary" ? theme.palette.primary.main : theme.palette.secondary1[60],
          })}
        />
        <LogoContainer sx={{ padding: 0, zIndex: 1, alignItems: "center", justifyContent: "center", display: "flex" }}>
          <LogoImg
            height={LOGO_SIZE_MAP[mergedProps.size]}
            width={LOGO_SIZE_MAP[mergedProps.size]}
            src={mergedProps.variant === "primary" ? outputLogoStripes : outputLogoStripesBlack}
            alt={t("global.logo_stripes_alt_text")}
          />
        </LogoContainer>
      </AnimationContainer>
      <Typography>{props.children}</Typography>
    </Container>
  );
}

export default FetchingFallback;
